import { useMemo, useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  ArrowCounterClockwiseIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useReportCanvasConfig } from '@/hooks/useReportCanvasConfig';
import type { ComponentRegistry } from './registry';
import { ComponentRegistryContext } from './registry-context';
import { ReportDataContext, type ReportDataset } from './ReportDataContext';
import type { GridLayout } from './types';
import { ReportCanvas } from './ReportCanvas';
import { ReportCanvasProvider } from './ReportCanvasProvider';
import { ReportCanvasView } from './ReportCanvasView';
import { ReportPalette } from './ReportPalette';
import { CanvasPromptBar } from './CanvasPromptBar';
import { useCanvasGeneration } from './useCanvasGeneration';
import { addComponent, removeItem, updateItem } from './layout';
import { ReportColorPaletteProvider } from './palette/ReportColorPaletteProvider';

interface ReportCanvasSectionProps {
  /** Stable id keying the persisted layouts. Never rename casually. */
  surfaceId: string;
  registry: ComponentRegistry;
  datasets: ReportDataset[];
  defaultDatasetKey: string;
  emptyHint?: string;
  /** Fallback layout when the user has no personal override. */
  developerDefault: GridLayout;
}

/**
 * A customizable analytics region: renders the effective layout (user
 * override ?? developer default) read-only, with an edit mode where users
 * rearrange charts via drag-and-drop. Saving persists a personal layout.
 * Its charts draw with the organization's default Report color palette.
 */
export function ReportCanvasSection(props: ReportCanvasSectionProps) {
  return (
    <ReportColorPaletteProvider>
      <CanvasSection {...props} />
    </ReportColorPaletteProvider>
  );
}

function CanvasSection({
  surfaceId,
  registry,
  datasets,
  defaultDatasetKey,
  emptyHint,
  developerDefault,
}: ReportCanvasSectionProps) {
  const gt = useGT();
  const {
    effectiveLayout,
    fallbackLayout,
    isLoading,
    isSaving,
    saveUserLayout,
    resetUserLayout,
  } = useReportCanvasConfig({ surfaceId, developerDefault, registry });

  // Edit mode holds a draft; null = viewing.
  const [draft, setDraft] = useState<GridLayout | null>(null);
  // Set when the draft is exactly the untouched developer default: saving
  // then DELETES the personal override instead of persisting a frozen copy,
  // so the user keeps tracking future default changes.
  const [draftIsReset, setDraftIsReset] = useState(false);
  const isEditing = draft !== null;

  const updateDraft = (updater: (prev: GridLayout) => GridLayout) => {
    setDraft((prev) => updater(prev ?? []));
    setDraftIsReset(false);
  };

  // ── Whole-canvas generation ("prompt the canvas") ─────────────────────────
  const generation = useCanvasGeneration({
    layout: draft,
    registry,
    datasets,
    defaultDatasetKey,
    onApply: (generated) => {
      // Functional update so a concurrent exit from edit mode wins (the canvas
      // is frozen while generating, so this is belt-and-braces).
      setDraft((prev) => (prev === null ? null : generated));
      setDraftIsReset(false);
    },
  });
  const { isGenerating } = generation;

  const dataValue = useMemo(
    () => ({ datasets, defaultDatasetKey, emptyHint }),
    [datasets, defaultDatasetKey, emptyHint],
  );

  if (isLoading) {
    return <Skeleton className="h-[300px] w-full" />;
  }

  if (!isEditing) {
    return (
      <div className="group/section">
        {/* A hover-revealed lane above the canvas, not an overlay: widgets
            render their own controls in the top-right corner (e.g. the aging
            chart's bar/stacked/pie toggle), so an absolutely-positioned button
            would sit on top of them. */}
        <div className="flex h-7 items-center justify-end opacity-0 transition-opacity group-hover/section:opacity-100">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs text-muted-foreground"
            onClick={() => setDraft(effectiveLayout)}
          >
            <PencilSimpleIcon className="h-3.5 w-3.5" />
            <T>Customize</T>
          </Button>
        </div>
        <ComponentRegistryContext.Provider value={registry}>
          <ReportDataContext.Provider value={dataValue}>
            {effectiveLayout.length === 0 ? (
              <button
                type="button"
                onClick={() => setDraft(effectiveLayout)}
                className="flex h-40 w-full flex-col items-center justify-center gap-2 rounded-lg border border-border bg-muted/30 text-sm text-muted-foreground hover:bg-muted/50 focus-visible:outline-2 focus-visible:outline-ring"
              >
                <T>No charts configured. Click to customize this section.</T>
              </button>
            ) : (
              <ReportCanvasView layout={effectiveLayout} />
            )}
          </ReportDataContext.Provider>
        </ComponentRegistryContext.Provider>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-primary/40 bg-muted/10">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
        <p className="text-sm font-medium">
          <T>Customize layout</T>
        </p>
        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs"
            disabled={isGenerating}
            onClick={() => {
              setDraft(fallbackLayout);
              setDraftIsReset(true);
            }}
            title={gt('Replace the draft with the default layout')}
          >
            <ArrowCounterClockwiseIcon className="h-3.5 w-3.5" />
            <T>Reset to default</T>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            disabled={isGenerating}
            onClick={() => {
              setDraft(null);
              setDraftIsReset(false);
              generation.reset();
            }}
          >
            <T>Cancel</T>
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs"
            disabled={isSaving || isGenerating}
            onClick={async () => {
              const saved = draftIsReset
                ? await resetUserLayout()
                : await saveUserLayout(draft);
              // On failure the hook has already toasted; keep the editor (and
              // the draft) open so the user's work isn't lost.
              if (!saved) return;
              setDraft(null);
              setDraftIsReset(false);
              generation.reset();
            }}
          >
            <T>Save</T>
          </Button>
        </div>
      </div>

      <CanvasPromptBar
        controls={generation}
        placeholder={gt(
          'Describe what you want to see — e.g. "key metrics on top, then aging by bucket next to top customers"',
        )}
      />

      <ReportCanvasProvider
        registry={registry}
        datasets={datasets}
        defaultDatasetKey={defaultDatasetKey}
        emptyHint={emptyHint}
        onLayoutChange={updateDraft}
      >
        {({ isAnyDragging }) => (
          // While a generation is in flight the canvas is frozen: a drag or
          // config edit landing mid-request would be silently overwritten when
          // the generated layout replaces the draft.
          <div
            className={`flex flex-col ${isGenerating ? 'pointer-events-none opacity-60' : ''}`}
            aria-busy={isGenerating}
          >
            <div className="max-h-56 overflow-y-auto border-b">
              <ReportPalette />
            </div>
            <ReportCanvas
              layout={draft}
              isAnyDragging={isAnyDragging}
              onDelete={(id) => updateDraft((prev) => removeItem(prev, id))}
              onUpdate={(id, component) =>
                updateDraft((prev) => updateItem(prev, id, component))
              }
              onArrange={updateDraft}
              onAdd={(type) =>
                updateDraft((prev) => addComponent(prev, registry, type).layout)
              }
            />
          </div>
        )}
      </ReportCanvasProvider>
    </div>
  );
}
